// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: what the current voice-mode answer came from. The Assistant cites with
// show_source, which lands as source cards on its reply; in voice mode the app points
// at the original instead (VoiceModePointer). The current answer is every Konvo
// message after the person's last message in the Assistant DM, this session only.

import {
	ASSISTANT_RESPONSE_PROVIDER,
	type AssistantDashboardBlock,
	type AssistantSourceItem,
	decodeAssistantResponseFields,
} from '@konvo/schema/src/domains/konvo/AssistantResponse';

interface VoiceSourceMessage {
	id: string;
	author: {id: string};
	embeds?: ReadonlyArray<{
		provider?: {name?: string | null} | null;
		author?: {name?: string | null} | null;
		fields?: ReadonlyArray<{name?: string | null; value?: string | null}> | null;
	}>;
}

/** The review card the Assistant opens for anything it prepares (KonvoAssistantActions.assistantActionCard). */
const ACTION_CARD_PROVIDER = 'Konvo:agenda';
const ACTION_CARD_SOURCE = 'Konvo · Action preview';

function isLaterOrSame(id: string, than: string): boolean {
	try {
		return BigInt(id) >= BigInt(than);
	} catch {
		return false;
	}
}

/** The Assistant's replies since the person last spoke (this session only), oldest first. */
function latestReplies(
	messages: ReadonlyArray<VoiceSourceMessage>,
	assistantId: string,
	sinceId: string,
): Array<VoiceSourceMessage> {
	const replies: Array<VoiceSourceMessage> = [];
	for (let index = messages.length - 1; index >= 0; index--) {
		const message = messages[index]!;
		if (!isLaterOrSame(message.id, sinceId) || message.author.id !== assistantId) break;
		replies.unshift(message);
	}
	return replies;
}

/** Review cards still waiting for Confirm on the Assistant's latest answer. */
export function pendingActionCardsOfLatestAnswer(
	messages: ReadonlyArray<VoiceSourceMessage>,
	assistantId: string,
	sinceId: string,
): Array<string> {
	const cards: Array<string> = [];
	for (const message of latestReplies(messages, assistantId, sinceId)) {
		for (const embed of message.embeds ?? []) {
			if (embed.provider?.name !== ACTION_CARD_PROVIDER || embed.author?.name !== ACTION_CARD_SOURCE) continue;
			const fields = embed.fields ?? [];
			if (fields.some((field) => field.name === 'done')) continue;
			const card = fields.find((field) => field.name === 'card')?.value?.trim();
			if (card) cards.push(card);
		}
	}
	return cards;
}

/** Sources on the Assistant's replies since the person last spoke, newest reply last. */
export function sourcesOfLatestAnswer(
	messages: ReadonlyArray<VoiceSourceMessage>,
	assistantId: string,
	sinceId: string,
): Array<AssistantSourceItem> {
	const replies = latestReplies(messages, assistantId, sinceId);
	const items: Array<AssistantSourceItem> = [];
	for (const message of replies) {
		const embed = message.embeds?.find((candidate) => candidate.provider?.name === ASSISTANT_RESPONSE_PROVIDER);
		const response = decodeAssistantResponseFields(embed?.fields);
		for (const block of response?.blocks ?? []) {
			if (block.type === 'source') items.push(...block.items);
		}
	}
	return items;
}

/** Dashboards on the Assistant's replies since the person last spoke, newest last. */
export function dashboardsOfLatestAnswer(
	messages: ReadonlyArray<VoiceSourceMessage>,
	assistantId: string,
	sinceId: string,
): Array<AssistantDashboardBlock> {
	const blocks: Array<AssistantDashboardBlock> = [];
	for (const message of latestReplies(messages, assistantId, sinceId)) {
		const embed = message.embeds?.find((candidate) => candidate.provider?.name === ASSISTANT_RESPONSE_PROVIDER);
		for (const block of decodeAssistantResponseFields(embed?.fields)?.blocks ?? []) {
			if (block.type === 'dashboard') blocks.push(block);
		}
	}
	return blocks;
}

/** A Konvo message the app can scroll to and point at. */
export function pointableKonvoSource(
	items: ReadonlyArray<AssistantSourceItem>,
): (AssistantSourceItem & {channel_id: string; message_id: string}) | null {
	for (const item of items) {
		if (item.kind === 'konvo' && item.channel_id && item.message_id) {
			return item as AssistantSourceItem & {channel_id: string; message_id: string};
		}
	}
	return null;
}
